<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";

import drillsConfig from "../drills/drills.json?json";
import DrillField from "./components/DrillField.vue";
import { DrillKind, StopAt } from "@acepoker/contract";
import { useRouter } from "vue-router";
import {
  getNextFields,
  getPossibleOptions,
  initConfigWithDefaults,
  isRuleSatisfied,
} from "../drills/configurator";

type DrillConfig = Record<string, string | string[] | undefined>;

const props = withDefaults(
  defineProps<{
    config?: DrillConfig;
  }>(),
  {
    config: () => ({}),
  },
);

// const config = ref(initConfigWithDefaults(drillsConfig, {}, {}));
const emits = defineEmits<{
  "config-updated": [DrillConfig];
}>();

const config = ref(initConfigWithDefaults(drillsConfig, props.config, props.config));

emits("config-updated", config.value);

const viewFields = computed(() => {
  const fields = [];
  const pendingFields = [drillsConfig];
  while (pendingFields.length > 0) {
    const field = pendingFields.shift();
    if (!field) {
      throw new Error("Invalid field");
    }

    const max =
      field.max ?? field.options.type === "dynamic"
        ? field.options.rules.find((rule) =>
            isRuleSatisfied(rule.condition, config.value),
          )?.max
        : undefined;

    fields.push({
      title: field.title,
      field: field.field,
      multiple: field.multiple,
      options: getPossibleOptions(field, config.value).filter(
        ({ hidden }) => !hidden,
      ),
      max,
      hidden: field.hidden,
    });
    pendingFields.push(...getNextFields(field, config.value));
  }
  return fields;
});

function updateField(field: string, value: string | string[] | undefined) {
  if (!value) {
    return;
  }

  config.value = initConfigWithDefaults(
    drillsConfig,
    { ...config.value },
    {
      ...config.value,
      [field]: value,
    },
  );

  emits("config-updated", config.value);
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <DrillField
      v-for="field in viewFields"
      :key="field.field"
      v-bind="field"
      :model-value="config[field.field]"
      @update:model-value="updateField(field.field, $event)"
    />
  </div>
</template>
